(() => {
  const revealNodes = Array.from(document.querySelectorAll("[data-reveal]"));
  const staggerContainers = Array.from(document.querySelectorAll("[data-stagger]"));
  const countNodes = Array.from(document.querySelectorAll("[data-count-up]"));
  const heroMedia = document.querySelector(".hero-media");
  const heroSection = document.querySelector(".hero");
  const mobileCta = document.querySelector(".mobile-cta");
  const magneticButtons = Array.from(document.querySelectorAll(".btn-primary"));
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const supportsIO = "IntersectionObserver" in window;
  const staggerDelay = 70;

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const setCountFinal = (node) => {
    const value = Number(node.dataset.countUp || 0);
    const prefix = node.dataset.countPrefix || "";
    const suffix = node.dataset.countSuffix || "";
    node.textContent = `${prefix}${value}${suffix}`;
  };

  const animateCount = (node) => {
    if (reducedMotion) {
      setCountFinal(node);
      return;
    }

    const target = Number(node.dataset.countUp || 0);
    const prefix = node.dataset.countPrefix || "";
    const suffix = node.dataset.countSuffix || "";
    const duration = Number(node.dataset.countDuration || 1100);
    const startedAt = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - startedAt) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const value = Math.round(target * eased);
      node.textContent = `${prefix}${value}${suffix}`;

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  };

  const revealChildren = (container) => {
    if (container.dataset.staggerDone === "1") return;
    container.dataset.staggerDone = "1";

    const children = Array.from(container.children);
    children.forEach((child, index) => {
      child.style.setProperty("--stagger-index", String(index));
      const delay = reducedMotion ? 0 : index * staggerDelay;
      window.setTimeout(() => {
        child.classList.add("is-visible");
      }, delay);
    });
  };

  const revealAll = () => {
    for (const node of revealNodes) {
      node.classList.add("is-visible");
    }

    for (const container of staggerContainers) {
      container.dataset.staggerDone = "1";
      Array.from(container.children).forEach((child, index) => {
        child.style.setProperty("--stagger-index", String(index));
        child.classList.add("is-visible");
      });
    }

    for (const node of countNodes) {
      setCountFinal(node);
    }

    if (mobileCta) {
      mobileCta.classList.add("is-visible");
    }
  };

  const setupRevealObserver = () => {
    if (!supportsIO) {
      revealAll();
      return null;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const target = entry.target;

          if (target.matches("[data-reveal]")) {
            target.classList.add("is-visible");
          }

          if (target.matches("[data-stagger]")) {
            revealChildren(target);
          }

          observer.unobserve(target);
        }
      },
      {
        rootMargin: "0px 0px -12% 0px",
        threshold: 0.16,
      }
    );

    [...revealNodes, ...staggerContainers].forEach((node) => observer.observe(node));
    return observer;
  };

  const setupCountObserver = () => {
    if (!countNodes.length) return null;

    if (!supportsIO || reducedMotion) {
      for (const node of countNodes) {
        setCountFinal(node);
      }
      return null;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          animateCount(entry.target);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.7,
      }
    );

    for (const node of countNodes) {
      observer.observe(node);
    }

    return observer;
  };

  const setupAccordions = () => {
    const details = Array.from(document.querySelectorAll("[data-accordion] details"));

    for (const detail of details) {
      detail.addEventListener("toggle", () => {
        if (!detail.open) return;

        for (const other of details) {
          if (other !== detail) other.open = false;
        }
      });
    }
  };

  const setupMagneticButtons = () => {
    if (reducedMotion || !magneticButtons.length) return;

    for (const button of magneticButtons) {
      let frame = 0;
      let lastEvent = null;

      const reset = () => {
        button.style.setProperty("--mag-x", "0px");
        button.style.setProperty("--mag-y", "0px");
      };

      const update = () => {
        frame = 0;
        if (!lastEvent) return;

        const rect = button.getBoundingClientRect();
        const offsetX = lastEvent.clientX - (rect.left + rect.width / 2);
        const offsetY = lastEvent.clientY - (rect.top + rect.height / 2);
        const maxOffset = 10;
        const x = clamp((offsetX / rect.width) * maxOffset, -maxOffset, maxOffset);
        const y = clamp((offsetY / rect.height) * maxOffset, -maxOffset, maxOffset);

        button.style.setProperty("--mag-x", `${x}px`);
        button.style.setProperty("--mag-y", `${y}px`);
      };

      button.addEventListener("pointermove", (event) => {
        lastEvent = event;
        if (frame) return;
        frame = window.requestAnimationFrame(update);
      });

      button.addEventListener("pointerleave", () => {
        lastEvent = null;
        if (frame) {
          window.cancelAnimationFrame(frame);
          frame = 0;
        }
        reset();
      });

      button.addEventListener("blur", reset);
    }
  };

  const setupHeroParallax = () => {
    if (reducedMotion || !heroMedia || !heroSection || !window.matchMedia("(pointer: fine)").matches) {
      return;
    }

    const browser = heroMedia.querySelector(".browser-mockup");
    if (!browser) return;

    let frame = 0;
    let lastPoint = null;

    const reset = () => {
      browser.style.setProperty("--mockup-tilt-x", "0");
      browser.style.setProperty("--mockup-tilt-y", "0");
    };

    const update = () => {
      frame = 0;
      if (!lastPoint) return;

      const rect = heroMedia.getBoundingClientRect();
      const x = (lastPoint.clientX - rect.left) / rect.width - 0.5;
      const y = (lastPoint.clientY - rect.top) / rect.height - 0.5;

      browser.style.setProperty("--mockup-tilt-x", `${clamp(x * 4, -4, 4).toFixed(2)}`);
      browser.style.setProperty("--mockup-tilt-y", `${clamp(-y * 3, -3, 3).toFixed(2)}`);
    };

    heroMedia.addEventListener("pointermove", (event) => {
      lastPoint = event;
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    });

    heroMedia.addEventListener("pointerleave", () => {
      lastPoint = null;
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }
      reset();
    });
  };

  const setupMobileCta = () => {
    if (!heroSection || !mobileCta || !supportsIO) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          mobileCta.classList.toggle("is-visible", !entry.isIntersecting);
        }
      },
      {
        threshold: 0.08,
      }
    );

    observer.observe(heroSection);
  };

  const setupNavToggle = () => {
    const toggle = document.querySelector(".nav-toggle");
    const nav = document.getElementById("site-nav");
    if (!toggle || !nav) return;

    const setOpen = (open) => {
      nav.classList.toggle("is-open", open);
      toggle.setAttribute("aria-expanded", open ? "true" : "false");
      toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");
    };

    toggle.addEventListener("click", () => {
      setOpen(!nav.classList.contains("is-open"));
    });

    nav.addEventListener("click", (event) => {
      if (event.target.closest("a")) setOpen(false);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") setOpen(false);
    });

    const wide = window.matchMedia("(min-width: 761px)");
    const onWide = (event) => {
      if (event.matches) setOpen(false);
    };
    if (wide.addEventListener) {
      wide.addEventListener("change", onWide);
    } else if (wide.addListener) {
      wide.addListener(onWide);
    }
  };

  const init = () => {
    setupAccordions();
    setupNavToggle();

    if (reducedMotion || !supportsIO) {
      revealAll();
      setupMobileCta();
      window.__MPLS_ANIM_READY__ = true;
      return;
    }

    setupRevealObserver();
    setupCountObserver();
    setupMagneticButtons();
    setupHeroParallax();
    setupMobileCta();

    window.setTimeout(revealAll, 5000);
    /* removed 120ms revealAll — it killed scroll reveals; IO + long safety net cover us */

    window.__MPLS_ANIM_READY__ = true;
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
